import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import TextField from '@material-ui/core/TextField'
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import moment from 'moment'
import React from 'react'
import { WaoDateTimeProps } from './Forms.Interfaces'
import { useSelector } from 'react-redux'
import { IRootReduxState } from 'types/reducers'
import { useDateTimeFormat } from 'utils/useDateTimeFormat'
import { useIntl } from 'react-intl'

type StyleProps = { isDarkMode: boolean }

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        margins: {
            marginTop: theme.spacing(0),
            marginBottom: theme.spacing(1),
            '& .MuiOutlinedInput-root': {
                backgroundColor: (props: StyleProps) => props.isDarkMode ? theme.palette.background.paper : 'transparent',
                color: (props: StyleProps) => props.isDarkMode ? theme.palette.text.primary : 'inherit',
            },
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (props: StyleProps) => props.isDarkMode ? theme.palette.divider : 'rgba(0, 0, 0, 0.23)',
            },
            '& .MuiInputLabel-outlined': {
                color: (props: StyleProps) => props.isDarkMode ? theme.palette.text.secondary : 'rgba(0, 0, 0, 0.6)',
            },
        },

        figmaPickerDialog: {
            width: 820,
            maxWidth: '820px !important',
            borderRadius: 8,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
        },

        figmaPickerRoot: {
            display: 'flex',
            background: (props: StyleProps) => props.isDarkMode ? '#2d3748' : '#FFFFFF',
            minHeight: 0,
            flex: 1,
            overflow: 'auto',
        },

        figmaCalendarPane: {
            width: 470,
            padding: '20px 22px 16px 22px',
            boxSizing: 'border-box',
        },

        figmaTimePane: {
            width: 350,
            borderLeft: (props: StyleProps) => props.isDarkMode ? '1px solid #4a5568' : '1px solid #D9DDE5',
            padding: '22px 26px 16px 26px',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: 18,
        },

        figmaHeaderRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 12,
        },

        figmaNavBtn: {
            width: 42,
            height: 42,
            border: (props: StyleProps) => props.isDarkMode ? '1px solid #4a5568' : '1px solid #D8DCE3',
            borderRadius: 8,
            background: (props: StyleProps) => props.isDarkMode ? '#3a4556' : '#FFFFFF',
            padding: 0,
        },

        figmaNavArrow: {
            fontSize: 28,
            lineHeight: 1,
            color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#8A93A5',
        },

        figmaMonthTitle: {
            fontSize: 18,
            lineHeight: '24px',
            fontWeight: 700,
            color: (props: StyleProps) => props.isDarkMode ? '#e2e8f0' : '#2B2F38',
        },

        figmaWeekHeader: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            marginBottom: 6,
        },

        figmaWeekday: {
            textAlign: 'center',
            fontSize: 14,
            lineHeight: '20px',
            color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#7A8496',
            fontWeight: 500,
            paddingBottom: 4,
        },

        figmaGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            rowGap: 6,
        },

        figmaDayCell: {
            width: 40,
            height: 40,
            border: 0,
            background: 'transparent',
            borderRadius: 8,
            fontSize: 14,
            lineHeight: '20px',
            color: (props: StyleProps) => props.isDarkMode ? '#cbd5e0' : '#2B2F38',
            cursor: 'pointer',
            justifySelf: 'center',
        },

        figmaDayMuted: {
            color: (props: StyleProps) => props.isDarkMode ? '#718096' : '#8C95A6',
        },

        figmaDayDisabled: {
            opacity: 0.3,
            cursor: 'not-allowed',
        },

        figmaDaySelected: {
            background: '#2563EB',
            color: '#FFFFFF',
            fontWeight: 600,
        },

        figmaFooterRow: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 12,
        },

        figmaLinkBtn: {
            border: 0,
            background: 'transparent',
            color: '#2563EB',
            fontSize: 14,
            lineHeight: '20px',
            cursor: 'pointer',
            padding: 0,
        },

        figmaTimeColumn: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            alignItems: 'center',
        },

        figmaTimeColumnScroll: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            alignItems: 'center',
            maxHeight: 314,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
        },

        figmaTimeItem: {
            minWidth: 54,
            height: 38,
            border: 0,
            background: 'transparent',
            borderRadius: 8,
            fontSize: 14,
            lineHeight: '20px',
            color: (props: StyleProps) => props.isDarkMode ? '#cbd5e0' : '#2B2F38',
            cursor: 'pointer',
            padding: '0 10px',
        },

        figmaTimeItemSelected: {
            background: '#2563EB',
            color: '#FFFFFF',
            fontWeight: 600,
        },

        figmaTimeItemDisabled: {
            opacity: 0.3,
            cursor: 'not-allowed',
        },

        figmaActions: {
            padding: '16px 20px',
            background: (props: StyleProps) => props.isDarkMode ? '#2d3748' : '#F9FAFB',
            borderTop: (props: StyleProps) => props.isDarkMode ? '1px solid #4a5568' : '1px solid #D9DDE5',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            flexShrink: 0,
            '& button': {
                textTransform: 'none',
                fontWeight: 500,
                padding: '10px 28px',
                borderRadius: 8,
                fontSize: '14px',
                border: 'none',
                cursor: 'pointer',
            },
            '& button:first-of-type': {
                color: (props: StyleProps) => props.isDarkMode ? '#a0aec0' : '#6B7280',
                backgroundColor: (props: StyleProps) => props.isDarkMode ? '#3a4556' : '#F3F4F6',
                '&:hover': {
                    backgroundColor: (props: StyleProps) => props.isDarkMode ? '#4a5568' : '#E5E7EB',
                },
            },
            '& button:last-of-type': {
                backgroundColor: '#2563EB',
                color: '#FFFFFF',
                fontWeight: 600,
                '&:hover': {
                    backgroundColor: '#1D4ED8',
                },
            },
        },
    })
)

export const WaoDateTime = ({ name, value, handleChange, label = undefined, disabled = false, minDate }: WaoDateTimeProps & { minDate?: string | Date | moment.Moment }) => {
    const minMoment = minDate ? moment(minDate) : null
    const isDarkMode = useSelector((state: IRootReduxState) => state.app.user.theme === 'dark')
    const timeFormatPref = useSelector((state: IRootReduxState) => state.app.user.timeFormat)
    const { formatDateTime } = useDateTimeFormat()
    const classes = useStyles({ isDarkMode })
    const intl = useIntl()
    const is24Hour = timeFormatPref === '24-hour'
    const momentLocale = intl.locale === 'zh' ? 'zh-cn' : intl.locale

    const parsed = value ? moment(value) : moment()
    const safeValue = parsed.isValid() ? parsed : moment()

    const [open, setOpen] = React.useState(false)
    const [displayMonth, setDisplayMonth] = React.useState(safeValue.clone().startOf('month'))
    const [selected, setSelected] = React.useState(safeValue.clone())
    const [preventReopen, setPreventReopen] = React.useState(false)

    React.useEffect(() => {
        const m = value ? moment(value) : moment()
        const next = m.isValid() ? m : moment()
        setSelected(next.clone())
        setDisplayMonth(next.clone().startOf('month'))
    }, [value])

    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
    const meridiems = [
        moment().locale(momentLocale).hours(6).format('A'),
        moment().locale(momentLocale).hours(18).format('A'),
    ]
    const weekDays = Array.from({ length: 7 }, (_, i) => moment().locale(momentLocale).day(i).format('dd'))

    const selectedHour = is24Hour ? selected.format('HH') : selected.format('hh')
    const selectedMinute = selected.format('mm')
    const selectedMeridiem = selected.locale(momentLocale).format('A')

    const isSelectedDayMinDay = minMoment != null && selected.isSame(minMoment, 'day')
    const minHour = minMoment ? minMoment.hour() : -1
    const minMinute = minMoment ? minMoment.minute() : -1
    const selectedHour24 = selected.hour()

    // Convert a displayed hour string (using the current AM/PM selection) to 24h
    const displayHourTo24 = (hh: string): number => {
        if (is24Hour) return parseInt(hh, 10)
        let h = parseInt(hh, 10)
        const isAM = selectedMeridiem === meridiems[0]
        if (isAM) { if (h === 12) h = 0 } else { if (h !== 12) h += 12 }
        return h
    }

    const isHourDisabled = (hh: string) => isSelectedDayMinDay && displayHourTo24(hh) < minHour
    const isMinuteDisabled = (mm: string) => isSelectedDayMinDay && selectedHour24 === minHour && parseInt(mm, 10) < minMinute
    const isAMDisabled = isSelectedDayMinDay && minHour >= 12

    // Full list of hours the user can scroll through, rather than a fixed 7-item window.
    const generateHoursList = () => {
      if (is24Hour) {
        return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
      }
      return ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11']
    }
    const hours = generateHoursList()

    const hourListRef = React.useRef<HTMLDivElement>(null)
    const minuteListRef = React.useRef<HTMLDivElement>(null)
    const selectedHourRef = React.useRef<HTMLButtonElement>(null)
    const selectedMinuteRef = React.useRef<HTMLButtonElement>(null)

    React.useEffect(() => {
        if (!open) return
        const scrollToCenter = (container: HTMLDivElement | null, item: HTMLButtonElement | null) => {
            if (!container || !item) return
            container.scrollTop = item.offsetTop - container.clientHeight / 2 + item.clientHeight / 2
        }
        // Deferred so the dialog has finished laying out before we measure offsets.
        const raf = requestAnimationFrame(() => {
            scrollToCenter(hourListRef.current, selectedHourRef.current)
            scrollToCenter(minuteListRef.current, selectedMinuteRef.current)
        })
        return () => cancelAnimationFrame(raf)
    }, [open])

    const monthLabel = displayMonth.clone().locale(momentLocale).format('MMMM YYYY')
    const start = displayMonth.clone().startOf('week')
    const end = displayMonth.clone().endOf('month').endOf('week')
    const days: moment.Moment[] = []
    const cursor = start.clone()

    while (cursor.isSameOrBefore(end, 'day')) {
        days.push(cursor.clone())
        cursor.add(1, 'day')
    }

    const applyAndClose = () => {
        // keepOffset=true preserves the picked wall-clock time and zone instead of
        // collapsing to UTC, so anything that reads the offset back (rather than
        // converting to another zone) shows exactly the time the user selected.
        handleChange && handleChange(name)(selected.clone().toISOString(true))
        setPreventReopen(true)
        setOpen(false)
        setTimeout(() => setPreventReopen(false), 150)
    }

    const clearValue = () => {
        handleChange && handleChange(name)('')
        setPreventReopen(true)
        setOpen(false)
        setTimeout(() => setPreventReopen(false), 150)
    }

    const updateDate = (day: moment.Moment) => {
        setSelected((prev) =>
            prev.clone().year(day.year()).month(day.month()).date(day.date())
        )
    }

    const updateHour = (hh: string) => {
        if (is24Hour) {
            setSelected((prev) => prev.clone().hour(parseInt(hh, 10)))
        } else {
            const mer = selected.locale(momentLocale).format('A')
            let h = parseInt(hh, 10)
            if (mer === meridiems[0]) {
                if (h === 12) h = 0
            } else {
                if (h !== 12) h += 12
            }
            setSelected((prev) => prev.clone().hour(h))
        }
    }

    const updateMinute = (mm: string) => {
        setSelected((prev) => prev.clone().minute(parseInt(mm, 10)))
    }

    const updateMeridiem = (mer: string) => {
        let h = selected.hour()
        const isAM = mer === meridiems[0]
        if (isAM && h >= 12) h -= 12
        if (!isAM && h < 12) h += 12
        setSelected((prev) => prev.clone().hour(h))
    }

    const inputValue = formatDateTime(value)

    return (
        <>
            <TextField
                className={classes.margins}
                fullWidth
                disabled={disabled}
                id={name}
                label={label || name}
                value={inputValue}
                onClick={() => {
                    if (disabled || preventReopen) return
                    setOpen(true)
                }}
                onFocus={() => {
                    if (disabled || preventReopen) return
                    setOpen(true)
                }}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                edge="end"
                                style={{ padding: 6, color: isDarkMode ? '#cbd5e0' : undefined }}
                                tabIndex={-1}
                            >
                                <CalendarTodayOutlinedIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                InputLabelProps={{ shrink: true }}
            />

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth={false}
                classes={{ paper: classes.figmaPickerDialog }}
                PaperProps={{
                    style: {
                        backgroundColor: isDarkMode ? '#2d3748' : '#FFFFFF',
                    }
                }}
            >
                <div className={classes.figmaPickerRoot}>
                    <div className={classes.figmaCalendarPane}>
                        <div className={classes.figmaHeaderRow}>
                            <IconButton
                                className={classes.figmaNavBtn}
                                disabled={minMoment != null && displayMonth.clone().subtract(1, 'month').endOf('month').isBefore(minMoment, 'day')}
                                onClick={() => setDisplayMonth((m) => m.clone().subtract(1, 'month'))}
                            >
                                <span className={classes.figmaNavArrow}>‹</span>
                            </IconButton>

                            <Typography className={classes.figmaMonthTitle}>{monthLabel}</Typography>

                            <IconButton
                                className={classes.figmaNavBtn}
                                onClick={() => setDisplayMonth((m) => m.clone().add(1, 'month'))}
                            >
                                <span className={classes.figmaNavArrow}>›</span>
                            </IconButton>
                        </div>

                        <div className={classes.figmaWeekHeader}>
                            {weekDays.map((d, i) => (
                                <div key={i} className={classes.figmaWeekday}>
                                    {d}
                                </div>
                            ))}
                        </div>

                        <div className={classes.figmaGrid}>
                            {days.map((day) => {
                                const inMonth = day.month() === displayMonth.month()
                                const isSelected = day.isSame(selected, 'day')
                                const isPastDay = minMoment != null && day.isBefore(minMoment, 'day')

                                return (
                                    <button
                                        key={day.format('YYYY-MM-DD')}
                                        type="button"
                                        disabled={isPastDay}
                                        className={[
                                            classes.figmaDayCell,
                                            !inMonth ? classes.figmaDayMuted : '',
                                            isSelected ? classes.figmaDaySelected : '',
                                            isPastDay ? classes.figmaDayDisabled : '',
                                        ].join(' ')}
                                        onClick={() => updateDate(day)}
                                    >
                                        {day.date()}
                                    </button>
                                )
                            })}
                        </div>

                        <div className={classes.figmaFooterRow}>
                            <button type="button" className={classes.figmaLinkBtn} onClick={clearValue}>
                                {intl.formatMessage({ id: 'waoDateTime.clear', defaultMessage: 'Clear' })}
                            </button>

                            <button
                                type="button"
                                className={classes.figmaLinkBtn}
                                onClick={() => {
                                    const now = moment()
                                    setSelected(now.clone())
                                    setDisplayMonth(now.clone().startOf('month'))
                                }}
                            >
                                {intl.formatMessage({ id: 'waoDateTime.today', defaultMessage: 'Today' })}
                            </button>
                        </div>
                    </div>

                    <div className={classes.figmaTimePane}>
                        <div ref={hourListRef} className={classes.figmaTimeColumnScroll}>
                            {hours.map((hh) => {
                                const hourDisabled = isHourDisabled(hh)
                                return (
                                    <button
                                        key={hh}
                                        ref={selectedHour === hh ? selectedHourRef : undefined}
                                        type="button"
                                        disabled={hourDisabled}
                                        className={[
                                            classes.figmaTimeItem,
                                            selectedHour === hh ? classes.figmaTimeItemSelected : '',
                                            hourDisabled ? classes.figmaTimeItemDisabled : '',
                                        ].join(' ')}
                                        onClick={() => updateHour(hh)}
                                    >
                                        {hh}
                                    </button>
                                )
                            })}
                        </div>

                        <div ref={minuteListRef} className={classes.figmaTimeColumnScroll}>
                            {minutes.map((mm) => {
                                const minuteDisabled = isMinuteDisabled(mm)
                                return (
                                    <button
                                        key={mm}
                                        ref={selectedMinute === mm ? selectedMinuteRef : undefined}
                                        type="button"
                                        disabled={minuteDisabled}
                                        className={[
                                            classes.figmaTimeItem,
                                            selectedMinute === mm ? classes.figmaTimeItemSelected : '',
                                            minuteDisabled ? classes.figmaTimeItemDisabled : '',
                                        ].join(' ')}
                                        onClick={() => updateMinute(mm)}
                                    >
                                        {mm}
                                    </button>
                                )
                            })}
                        </div>

                        {!is24Hour && (
                            <div className={classes.figmaTimeColumn}>
                                {meridiems.map((mer, idx) => {
                                    const merDisabled = idx === 0 && isAMDisabled
                                    return (
                                        <button
                                            key={mer}
                                            type="button"
                                            disabled={merDisabled}
                                            className={[
                                                classes.figmaTimeItem,
                                                selectedMeridiem === mer ? classes.figmaTimeItemSelected : '',
                                                merDisabled ? classes.figmaTimeItemDisabled : '',
                                            ].join(' ')}
                                            onClick={() => updateMeridiem(mer)}
                                        >
                                            {mer}
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <DialogActions className={classes.figmaActions}>
                    <Button
                        onClick={() => {
                            setPreventReopen(true)
                            setOpen(false)
                            setTimeout(() => setPreventReopen(false), 150)
                        }}
                    >
                        {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
                    </Button>
                    <Button color="primary" onClick={applyAndClose} disabled={minMoment != null && selected.isBefore(minMoment)}>
                        {intl.formatMessage({ id: 'ok', defaultMessage: 'OK' })}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default WaoDateTime